import React from 'react';
import { Link } from 'react-router-dom';
import TroubleshootingPageLayout from '../components/TroubleshootingPageLayout';

const TOC = [
  { id: 'overview', label: 'Overview' },
  { id: 'amounts', label: 'Amount decimals' },
  { id: 'encoding', label: 'URL encoding' },
  { id: 'time-currency', label: 'Time zones & currency' },
  { id: 'udf', label: 'UDF parsing' },
  { id: 'idempotency', label: 'Idempotency' },
];

const IntegrationIssues = () => (
  <TroubleshootingPageLayout
    currentId="integration-issues"
    title="Integration Issues"
    subtitle="The handful of subtle problems that trip up 90% of first integrations — and how to dodge them."
    toc={TOC}
    mainTestId="ts-integration-issues-page"
    sidebarTestId="ts-integration-issues-sidebar"
  >
    <section id="overview" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Overview</h2>
      <p className="text-gray-700 leading-relaxed">
        None of these break the build outright — which is why they're painful. Each has been responsible for a "why is my refund £100× larger than expected?"-scale incident in the past.
      </p>
    </section>

    <section id="amounts" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Amount decimals (minor units)</h2>
      <p className="text-gray-700 mb-3 leading-relaxed">
        Amounts are always <strong>integer minor units</strong>. Never send a decimal. The exponent varies by currency — see <Link to="/technical-documentation/appendix/currency-codes" className="text-[#1F4E79] font-semibold underline">Currency Codes</Link>.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-left border border-gray-200 rounded-lg overflow-hidden">
          <thead className="bg-gray-100 border-b border-gray-200">
            <tr>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Display price</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Currency</th>
              <th className="p-3 text-xs font-bold text-gray-700 uppercase tracking-wider">Send as</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-gray-200"><td className="p-3 text-sm">€123.45</td><td className="p-3 text-sm font-mono">EUR (-2)</td><td className="p-3 text-sm font-mono text-emerald-700">12345</td></tr>
            <tr className="border-b border-gray-200"><td className="p-3 text-sm">¥500</td><td className="p-3 text-sm font-mono">JPY (0)</td><td className="p-3 text-sm font-mono text-emerald-700">500</td></tr>
            <tr className="border-b border-gray-200"><td className="p-3 text-sm">د.ك 1.234</td><td className="p-3 text-sm font-mono">KWD (-3)</td><td className="p-3 text-sm font-mono text-emerald-700">1234</td></tr>
            <tr><td className="p-3 text-sm">£99.99</td><td className="p-3 text-sm font-mono">GBP (-2)</td><td className="p-3 text-sm font-mono text-emerald-700">9999</td></tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
        <strong>Common bug:</strong> <code className="bg-rose-100 px-1 rounded">Math.round(amount * 100)</code> breaks for JPY (no decimals) and KWD (3 decimals). Always consult the currency exponent.
      </div>
    </section>

    <section id="encoding" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">URL encoding</h2>
      <p className="text-gray-700 mb-3 leading-relaxed">
        Customer names, addresses and email fields often contain &amp;, =, +, spaces — all of which break form-encoded POSTs if not encoded. Symptom: error <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0016 / 0017</code> on random orders.
      </p>
      <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 pl-2">
        <li>PHP: <code className="bg-gray-100 px-1 rounded font-mono text-xs">http_build_query($fields)</code> — not manual concatenation.</li>
        <li>C# / .NET: <code className="bg-gray-100 px-1 rounded font-mono text-xs">new FormUrlEncodedContent(dict)</code>.</li>
        <li>Node.js: <code className="bg-gray-100 px-1 rounded font-mono text-xs">new URLSearchParams(obj).toString()</code>.</li>
      </ul>
      <p className="text-sm text-gray-600 mt-3">Full character-&gt;hex table: <Link to="/technical-documentation/appendix/url-encoding" className="text-[#1F4E79] font-semibold underline">URL Encoding</Link>.</p>
    </section>

    <section id="time-currency" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Time zones &amp; currency codes</h2>
      <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 pl-2 leading-relaxed">
        <li><strong>Timestamps are UTC.</strong> Confirmation notifications use ISO 8601 in UTC. Convert on your side if you need local time — don't assume server zone.</li>
        <li><strong>Currency is ISO 4217 numeric</strong> on the wire (e.g. EUR = <code className="bg-gray-100 px-1 rounded font-mono text-xs">978</code>, GBP = <code className="bg-gray-100 px-1 rounded font-mono text-xs">826</code>, USD = <code className="bg-gray-100 px-1 rounded font-mono text-xs">840</code>). Full list: <Link to="/technical-documentation/appendix/currency-codes" className="text-[#1F4E79] font-semibold underline">Currency Codes</Link>.</li>
        <li><strong>Country is ISO 3166 alpha-2</strong> on the wire (e.g. GB, US, IE). Full list: <Link to="/technical-documentation/appendix/country-codes" className="text-[#1F4E79] font-semibold underline">Country Codes</Link>.</li>
      </ul>
    </section>

    <section id="udf" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">UDF parsing</h2>
      <p className="text-gray-700 mb-3 leading-relaxed">
        UDF is a single pipe-delimited field echoed back on every Sale response and notification. Great for stuffing your internal order ID, but three rules:
      </p>
      <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 pl-2">
        <li>No pipes in the values themselves (split will explode).</li>
        <li>Total length after URL-encoding: 500 chars.</li>
        <li>Order is positional — your parser should tolerate empty slots (<code className="bg-gray-100 px-1 rounded font-mono text-xs">||</code>) rather than crash.</li>
      </ul>
    </section>

    <section id="idempotency" className="bg-white/80 backdrop-blur-sm rounded-lg shadow-md p-8 mb-8 scroll-mt-28">
      <h2 className="text-2xl font-bold text-gray-900 mb-3">Idempotency</h2>
      <p className="text-gray-700 leading-relaxed">
        Use a fresh <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">MessageID</code> for every outbound request, even on retry. Use a single <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">TransactionCode</code> for the entire lifecycle of one customer intent (Sale → Confirm → Refund all reference the same TransactionCode). Never reuse TransactionCode across unrelated intents — it'll collide with error <code className="bg-gray-100 px-1.5 py-0.5 rounded text-xs font-mono">0006</code>.
      </p>
    </section>
  </TroubleshootingPageLayout>
);

export default IntegrationIssues;
